import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { ensureGhlPreconnect } from "@/lib/ghl-preconnect";
import { SITE } from "@/lib/site";
import { phCapture } from "@/lib/posthog";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/G3S5bvT5Fyz0ZgMSJTqf";
const EMBED_SCRIPT = "https://api.leadconnectorhq.com/js/form_embed.js";

function loadEmbedScript() {
  if (typeof document === "undefined") return;
  ensureGhlPreconnect();
  if (document.querySelector('script[src*="form_embed.js"]')) return;
  const s = document.createElement("script");
  s.src = EMBED_SCRIPT;
  s.type = "text/javascript";
  s.async = true;
  document.body.appendChild(s);
}

export const Route = createFileRoute("/solar-follow-up")({
  head: () => ({
    meta: [
      { title: "Book Your Solar Follow-Up Call With CRS Energy" },
      {
        name: "description",
        content:
          "Book your solar follow-up call with the CRS Energy team to discuss your project.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Book Your Solar Follow-Up Call With CRS Energy" },
      {
        property: "og:description",
        content:
          "Book your solar follow-up call with the CRS Energy team to discuss your project.",
      },
    ],
  }),
  component: SolarFollowUpPage,
});

function SolarFollowUpPage() {
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadEmbedScript();
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      try {
        const origin = event.origin || "";
        if (!/leadconnectorhq\.com|msgsndr\.com/i.test(origin)) return;
        const data = event.data;
        const text = typeof data === "string" ? data : JSON.stringify(data ?? "");
        if (
          /appointment[._-]?(scheduled|booked|confirmed)|booking[._-]?(success|confirmed|complete)|formSubmitted/i.test(
            text,
          )
        ) {
          phCapture("booking_created", {
            booking_source: "solar_follow_up_page",
            calendar: "ghl",
          });
          navigate({ to: "/thank-you" });
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [navigate]);

  return (
    <main className="min-h-[100svh] bg-background">
      <div className="mx-auto w-full max-w-3xl px-0 sm:px-4 py-0 sm:py-8">
        <header className="hidden sm:block mb-6 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-primary">
            Book Your Solar Follow-Up Call With CRS Energy
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Select a time that works for you and our team will call you back.
          </p>
        </header>

        <div
          className="relative w-full sm:rounded-2xl overflow-hidden sm:border sm:border-border bg-background sm:shadow-sm"
          aria-busy={!loaded}
        >
          <iframe
            src={BOOKING_URL}
            title="Book Your Solar Follow-Up Call With CRS Energy"
            scrolling="yes"
            onLoad={() => setLoaded(true)}
            className="block w-full border-0 h-[100svh] sm:h-[80vh] sm:min-h-[720px]"
          />
          {!loaded && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background px-6 text-center">
              <div className="max-w-sm">
                <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
                <p className="text-base font-semibold text-foreground">
                  Opening The Booking Calendar
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  This should only take a moment. If it is slow, you can call us instead.
                </p>
                <a
                  href={SITE.phoneTel}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground hover:bg-accent/90"
                >
                  <Phone className="h-4 w-4" aria-hidden />
                  Call Now
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
